/**
 * RecentActivitySection.tsx
 *
 * File-level:
 * - Renders the "Recent Activity" card on the dashboard.
 * - Accepts flexible incoming activity shapes (legacy and modern) and normalizes them.
 * - If no recentActivity prop is provided or it's empty, this component will attempt to
 *   fetch recent activities for the currently authenticated user (via useAuthStore).
 *
 * Notes:
 * - Provides skeletons for loading state, fetch fallback, and a clear empty-state message.
 */

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, Eye, Heart, MessageCircle, UserCheck, RefreshCw, AlertTriangle } from 'lucide-react';
import { formatStoredDate } from '@/utils/formatStoredDate';
import useAuthStore from '@/stores/useAuthStore';
import { API_CONFIG } from '../../config/api.config';

/**
 * NormalizedActivity
 *
 * @interface NormalizedActivity
 * @description Internal normalized shape for activity items rendered by this component.
 */
interface NormalizedActivity {
  id: string;
  type?: string;
  title?: string;
  description?: string;
  timestamp?: string;
  icon?: React.ReactNode;
  userId?: string; // User ID associated with the activity (actorId)
  actorFirstName?: string; // Pre-fetched from backend
  actorLastName?: string; // Pre-fetched from backend
}

/**
 * Props for RecentActivitySection
 *
 * @interface RecentActivitySectionProps
 */
interface RecentActivitySectionProps {
  recentActivity?: any[] | null;
  loading?: boolean;
  /**
   * Optional: maximum number of items to fetch when falling back to client fetch.
   * Defaults to 20 (matches backend default).
   */
  fetchLimit?: number;
}

/**
 * normalizeActivity
 *
 * @function normalizeActivity
 * @param a - incoming activity object (any shape)
 * @returns NormalizedActivity - normal form used by UI
 *
 * @description
 * - Supports modern shapes: { id, title, description, timestamp, icon, type }
 * - Supports legacy shapes: { id, type, message, time, user }
 */
function normalizeActivity(a: any): NormalizedActivity {
  const id = String(a?.id ?? a?.activityId ?? a?.user ?? Math.random().toString());
  const type = a?.type ?? a?.activityType ?? 'activity';
  const title =
    a?.title ??
    a?.eventTitle ??
    (typeof a?.message === 'string' ? a.message : undefined) ??
    undefined;
  const description = a?.description ?? a?.details ?? undefined;
  const timestamp = a?.timestamp ?? a?.time ?? a?.createdAt ?? a?.viewDate ?? undefined;
  // Use actorId (who performed the action) instead of userId (who received it)
  const userId = a?.actorId ?? a?.fromUserId ?? a?.user ?? a?.userId ?? undefined;
  const actorFirstName = a?.actorFirstName ?? undefined;
  const actorLastName = a?.actorLastName ?? undefined;

  let icon: React.ReactNode | undefined = a?.icon;
  if (!icon) {
    switch (type) {
      case 'profile_view':
      case 'view':
        icon = <Eye className="h-4 w-4 text-blue-600" />;
        break;
      case 'shortlist':
        icon = <Heart className="h-4 w-4 text-red-600" />;
        break;
      case 'message':
        icon = <MessageCircle className="h-4 w-4 text-purple-600" />;
        break;
      case 'interest':
        icon = <UserCheck className="h-4 w-4 text-green-600" />;
        break;
      default:
        icon = <Activity className="h-4 w-4 text-gray-600" />;
        break;
    }
  }

  return {
    id,
    type,
    title,
    description,
    timestamp,
    icon,
    userId,
    actorFirstName,
    actorLastName,
  };
}

/**
 * RecentActivitySection component
 *
 * @component RecentActivitySection
 * @param props - RecentActivitySectionProps
 * @returns JSX.Element
 */
export default function RecentActivitySection({
  recentActivity = null,
  loading = false,
  fetchLimit = 20,
}: RecentActivitySectionProps) {
  const authUserId = useAuthStore((s) => s.userId);
  const apiBase = API_CONFIG.getApiBaseUrl();

  const [fetched, setFetched] = useState<NormalizedActivity[] | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // State for user names
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const fetchedUserIdsRef = useRef<Set<string>>(new Set());

  // Decide source: prop (if provided and non-null) takes precedence; otherwise fallback to fetched data.
  const sourceArray = useMemo(() => {
    if (Array.isArray(recentActivity) && recentActivity.length > 0) return recentActivity;
    // If recentActivity is explicitly an empty array, we'll still try to fetch.
    if (fetched && fetched.length > 0) return fetched;
    return [];
  }, [recentActivity, fetched]);

  const activities: NormalizedActivity[] = Array.isArray(sourceArray) && sourceArray.length > 0
    ? sourceArray.map(normalizeActivity)
    : [];

  const skeletons = [1, 2, 3];

  /**
   * fetchRecentActivities
   *
   * @description Attempt to fetch recent activities for the current authUserId.
   * Updates local state and errors. Silently returns if no userId present.
   */
  async function fetchRecentActivities() {
    if (!authUserId) {
      // No user id available; nothing to fetch.
      return;
    }
    setFetching(true);
    setFetchError(null);
    try {
      const url = `${apiBase.replace(/\/$/, '')}/user-activities/recent/${encodeURIComponent(authUserId)}?limit=${fetchLimit}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`Server responded ${res.status}: ${text}`);
      }
      const data = await res.json().catch(() => null);
      // backend shape: { success: true, activities: [...] } or direct array
      let rows: any[] = [];
      if (Array.isArray(data)) rows = data;
      else if (data && Array.isArray(data.activities)) rows = data.activities;
      else if (data && Array.isArray(data.recordset)) rows = data.recordset;
      else rows = [];

      setFetched(rows.map(normalizeActivity));
    } catch (err: any) {
      setFetchError(String(err?.message ?? err ?? 'Failed to fetch activities'));
      setFetched([]);
    } finally {
      setFetching(false);
    }
  }

  /**
   * fetchUserName
   *
   * @description Fetch user name from /users endpoint by userId
   * @param userId - The user ID to fetch name for
   */
  async function fetchUserName(userId: string) {
    if (!userId || fetchedUserIdsRef.current.has(userId) || userNames[userId]) {
      return;
    }

    fetchedUserIdsRef.current.add(userId);
    try {
      const url = `${apiBase.replace(/\/$/, '')}/users/${encodeURIComponent(userId)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`Failed to fetch user ${userId}`);
      }
      const data = await res.json();
      const user = data?.user ?? data?.data ?? data;
      const firstName = user?.firstName ?? user?.first_name ?? '';
      const lastName = user?.lastName ?? user?.last_name ?? '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      if (fullName) {
        setUserNames(prev => ({ ...prev, [userId]: fullName }));
      }
    } catch (err) {
      console.error(`Failed to fetch name for user ${userId}:`, err);
      fetchedUserIdsRef.current.delete(userId); // Allow retry on error
    }
  }

  // If the parent didn't provide activities or provided an empty array, attempt client-side fetch.
  useEffect(() => {
    const shouldFetch =
      (recentActivity == null || (Array.isArray(recentActivity) && recentActivity.length === 0)) &&
      !!authUserId &&
      !fetching &&
      fetched === null;
    if (shouldFetch) {
      fetchRecentActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserId, recentActivity]);

  // Fetch user names for all activities with userId
  useEffect(() => {
    const userIds = activities
      .filter(a => {
        // Only fetch if we don't have the name from backend or from cache
        if (!a.userId) return false;
        if (a.actorFirstName && a.actorLastName) {
          // Store pre-fetched name in cache
          const fullName = `${a.actorFirstName} ${a.actorLastName}`.trim();
          if (!userNames[a.userId]) {
            setUserNames(prev => ({ ...prev, [a.userId!]: fullName }));
          }
          return false;
        }
        return !userNames[a.userId] && !fetchedUserIdsRef.current.has(a.userId);
      })
      .map(a => a.userId!);
    
    userIds.forEach(userId => {
      fetchUserName(userId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities.length, JSON.stringify(activities.map(a => a.userId))]);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Recent Activity
          </div>
          <Badge variant="secondary" className="text-xs">
            {loading || fetching ? '...' : activities.length}
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {loading || fetching ? (
          <div className="space-y-4">
            {skeletons.map((i) => (
              <div key={i} className="flex items-start space-x-3 animate-pulse">
                <div className="w-8 h-8 bg-gray-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : fetchError ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              <div className="flex-1">
                Failed to load recent activity: <span className="font-medium">{fetchError}</span>
                <div className="text-xs text-gray-500 mt-1">Check API connectivity or open the Debug Panel.</div>
              </div>
              <button
                onClick={() => {
                  setFetched(null);
                  setFetchError(null);
                  fetchRecentActivities();
                }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded bg-slate-50 hover:bg-slate-100 text-sm border"
              >
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>

            {/* If we also have no activities to show, keep the empty state below */}
            {activities.length === 0 && (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 text-sm">No recent activity</p>
                <p className="text-gray-500 text-xs mt-1">
                  Recent interactions will appear here. If this seems incorrect, open the Debug Panel to inspect API responses and localStorage.
                </p>
              </div>
            )}
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">No recent activity</p>
            <p className="text-gray-500 text-xs mt-1">
              Recent interactions will appear here. If this seems incorrect, open the Debug Panel to inspect API responses and localStorage.
            </p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {activity.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-900">
                      {userNames[activity.userId || ''] ? (
                        <>
                          <span className="font-bold">{userNames[activity.userId || '']}</span>
                          <span className="text-gray-500 font-normal"> ({activity.userId}) </span>
                          <span className="font-normal">
                            {(() => {
                              // Check description for specific actions first
                              const desc = activity.description || '';
                              
                              // Handle interest acceptance
                              if (desc.toLowerCase().includes('accept interest')) {
                                return 'accepted your interest';
                              }
                              
                              // Generate message based on activity type
                              const type = activity.type || '';
                              switch (type) {
                                case 'profile_view':
                                case 'view':
                                  return 'viewed your profile';
                                case 'shortlist':
                                  return 'shortlisted your profile';
                                case 'interest':
                                case 'interest_received':
                                  return 'sent you interest';
                                case 'message':
                                  return 'sent you a message';
                                case 'match_suggested':
                                  return 'is a suggested match';
                                default:
                                  return 'interacted with your profile';
                              }
                            })()}
                          </span>
                          {(() => {
                            // Only show description if it contains meaningful additional info
                            const desc = activity.description || '';
                            const userId = activity.userId || '';
                            
                            // Skip if description contains userId
                            if (desc && userId && desc.includes(userId)) {
                              return null;
                            }
                            
                            // Skip generic/system messages
                            if (desc.match(/expressed interest|viewed.*profile|shortlisted|sent.*message|accept interest.*for id|for id \d+/i)) {
                              return null;
                            }
                            
                            // Show if there's unique, meaningful content
                            return desc ? <span className="text-gray-600 font-normal"> - {desc}</span> : null;
                          })()}
                        </>
                      ) : (
                        activity.title ?? activity.description ?? 'Activity'
                      )}
                    </p>
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                      {activity.timestamp ? formatStoredDate(activity.timestamp) : '—'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}